import React from 'react';
import { useSelector } from 'react-redux'
import { HashLink as Link } from 'react-router-hash-link';
import { GlobalStore } from '../../../store';
import User from '../../../models/User';
import { CheckMark, Clock, Copy, Cross, Envelope, QuestionMark } from '../../../viewcomponents/FontAwesome';

export default (): JSX.Element | null => {

  const user = useSelector<GlobalStore, User | null>(store => store.user);

  const actionButtons = [
    { cls: 'red', icon: <Cross />, label: 'Kick' },
    { cls: 'blue', icon: <Envelope />, label: 'Message' },
    { cls: 'theme', icon: <CheckMark />, label: 'Help' },
    { cls: 'yellow', icon: <Clock />, label: 'Call dibs' },
    { cls: 'purple', icon: <QuestionMark />, label: 'Bad location' },
    { cls: 'gray', icon: <Copy />, label: 'Copy' },
  ];

  return (
    user?.isAdministrator || user?.isTeacher() || user?.isAssistant()
      ? <div className="card p-3 mb-3">
          <h2 id="assistant">Assistants</h2>

          <p>
            Queue assistants help with moderating specific queues.
            In addition to removing specific users,
            they are also able to lock and <Link smooth to="/help#purge">purge</Link> queues, as well as sending messages and interact
            with the users in the queue.
          </p>
          <p>
            <em>
              Note: All the assistants privileges are located in the queue.
              You get there by choosing the right queue from the list of <Link smooth to="/list">queues</Link>.
            </em>
          </p>

          {/* ===================== GROUP 1: presence & panels ===================== */}
          <h3 id="presence">On duty &amp; panels</h3>

          <h4 id="checkin">Checking in and out</h4>
          <p>
            When you arrive at a lab session, click <em>Check in for duty</em> below the queue entry form.
            This marks you as on duty, making you visible in the <Link smooth to="/help#onDuty">On duty</Link> list,
            and is used for attendance tracking.
          </p>
          <p>
            If you checked in by mistake, click <em>Cancel check-in</em> to undo it — this removes the record entirely.
          </p>
          <p>
            If you need to leave before the session ends, click <em>Check out from queue</em>.
            This removes you from the on duty list and records your departure time.
          </p>

          <h4 id="onDuty">On duty</h4>
          <p>
            The <em>On duty</em> panel shows which TAs are currently checked in and active in the queue.
            A TA is considered on duty if they have checked in and have had activity within the last hour.
            The panel is only visible to assistants and teachers, and is collapsed by default on small screens.
          </p>

          <h4 id="todayBookings">Today's bookings</h4>
          <p>
            The <em>Today's bookings</em> panel shows all pre-booked lab slots for the current day.
            Upcoming bookings are listed first; past bookings are shown greyed out below.
            This gives assistants an overview of which students have reserved slots and when they are expected.
          </p>
          <p>
            Students with an active booking will have their slot time shown as a badge on their queue entry.
            Within 30 minutes of their booking time, their entry is moved to the top of the queue automatically.
          </p>

          {/* ===================== GROUP 2: per-student action buttons ===================== */}
          <h3 id="actionButtons">Acting on a student</h3>
          <p>
            When you expand a queue entry — double-click it, or a single tap on a phone — a row of coloured
            buttons appears. Each one is described below.
          </p>

          <div className="row my-2 text-center">
            {actionButtons.map(b => (
              <div className="col px-2 my-1" key={b.label}>
                <div className={`${b.cls} text-white`} style={{ lineHeight: '2.5em', borderRadius: '2px' }}>
                  {b.icon}
                </div>
                <div style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>{b.label}</div>
              </div>
            ))}
          </div>

          <h4 id="kick">Kicking a user</h4>
          <p>
            To remove a queue position, first double-click the user you wish to remove and thereafter click the appering red
            button with a cross on it.
            Be careful when removing users from the queue, as this action can not be reverted.
          </p>
          <p>
            <em>
              Note: If you are accessing the service through a recognized smartphone device, you will only have to click once
              to show the option to kick a user.
            </em>
          </p>

          <h4 id="message">Message a user</h4>
          <p>
            In order to send a message to a certain user in the queue, access the options as you would <Link smooth to="/help#kick">kicking</Link> a
            user, and click the blue button with an envelope on it.
            Enter a message in the window that opens up and click <em>Send</em>.
          </p>
          <p>
            To send a message to everyone in the queue, see <Link smooth to="/help#broadcast">broadcast</Link>.
          </p>

          <h4 id="help">Help a user</h4>
          <p>
            By helping a user, you mark them using the green button with a checkmark accessed in the same way as you would access the
            button to <Link smooth to="/help#kick">kick</Link> someone.
            When a user is receiving help, their row will have a green background, notifing any other assistant that they can move to the next person
            in the queue.
          </p>

          <h4 id="callDibs">Call dibs</h4>
          <p>
            <em>Call dibs</em> lets you claim a student before you have reached them, so other assistants
            know you are on your way and don't walk over to the same person.
            It is the yellow button with a clock icon, next to the green <Link smooth to="/help#help">help</Link> button,
            accessed the same way (double-click the entry, or a single tap on a phone).
          </p>
          <p>
            While you have dibs on a student, they see a reassuring <em>help is on the way</em>, and teachers — and
            assistants, unless <Link smooth to="/help#showHelper">Hide helper to assistants</Link> is on — can see who
            claimed it. Click the button again (now grey) to release the claim if you change your mind.
            Once you actually start <Link smooth to="/help#help">helping</Link>, that takes over from the dibs;
            the button is unavailable while a student is being helped.
          </p>

          <h4 id="badLocation">Bad location</h4>
          <p>
            If an assistant is unable to find a given user because they have entered a location that is not precise enough, the
            assistant has the option to mark the user for <em>Bad location</em>.
            Marking a user for <em>bad location</em> is done by clicking the purple button with a question-mark on it.
            The button is found at the same place as the one used to <Link smooth to="/help#kick">kick</Link> a user.
          </p>
          <p>
            The user will then receive a message prompting them to edit their location.
            The user will then have a red color until they have edited their location.
          </p>

          <h4 id="copyDetails">Copy student details</h4>
          <p>
            The grey button with a copy icon copies the student's username, real name and comment to your
            clipboard, separated by tabs so they line up in columns when pasted.
            It is handy for pasting straight into a grading sheet, spreadsheet or Canvas.
            The button is found in the same row as the one used to <Link smooth to="/help#kick">kick</Link> a user,
            and briefly shows <em>Copied!</em> once the details are on your clipboard.
          </p>

          {/* ===================== GROUP 3: queue options (dropdown) ===================== */}
          <h3 id="queueOptions">Queue options</h3>
          <p id="assistantOptions">
            Below the <em>join the queue</em> form, assistants find a dropdown of administrative options.
            Each opens a window or confirmation popup; here is what they do:
          </p>

          <table className="table table-striped">
            <thead>
              <tr><th>Option</th><th>What it does</th></tr>
            </thead>
            <tbody>
              <tr id="broadcast">
                <td><strong>Broadcast</strong></td>
                <td>Send a message to <em>everyone</em> in the queue.</td>
              </tr>
              <tr id="broadcastFaculty">
                <td><strong>Broadcast faculty</strong></td>
                <td>Send a message only to the assistants and teachers in the room.</td>
              </tr>
              <tr id="setMOTD">
                <td><strong>Set MOTD</strong></td>
                <td>
                  Message of the Day — shown to each user <em>once</em>, as they enter the room
                  (e.g. "presentations only today"). For something shown continuously, use <em>Set queue info</em>.
                </td>
              </tr>
              <tr id="setInfo">
                <td><strong>Set queue info</strong></td>
                <td>A notice shown <em>continuously</em> at the top of the queue page. Only a teacher can remove it.</td>
              </tr>
              <tr id="lock">
                <td><strong>Lock queue</strong></td>
                <td>Stops new students joining; people already in the queue stay. Pick <em>Unlock</em> the same way to reopen it.</td>
              </tr>
              <tr id="purge">
                <td><strong>Purge queue</strong></td>
                <td>Removes everyone currently in the queue. Cannot be undone.</td>
              </tr>
              <tr id="sounds">
                <td><strong>Sounds</strong></td>
                <td>Plays a sound when the queue goes from empty to one student. Per browser tab; resets when you navigate away.</td>
              </tr>
              <tr id="showHelper">
                <td><strong>Hide helper to assistants</strong></td>
                <td>Teachers only — hides which TA is helping a student from other assistants (teachers always see the name).</td>
              </tr>
            </tbody>
          </table>
        </div>
      : null
  );
};
